/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, type KeyboardEventHandler } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { jsx } from '@atlaskit/css';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { MentionsPlugin } from '@atlaskit/editor-plugins/mentions';
import MentionIcon from '@atlaskit/icon/core/mention';

import { useFireAnalyticsEvent } from '../../hooks/useAnalyticsEvent';

import messages from './messages';

type Props = {
	editorApi?: PublicPluginAPI<[MentionsPlugin]>;
	promptType: 'user-input' | 'interrogate';
};

export const MentionButton = ({ editorApi, promptType }: Props) => {
	const { formatMessage } = useIntl();

	const { currentHandler, typeAheadHandler } = useSharedPluginStateWithSelector(
		editorApi,
		['typeAhead', 'mention'],
		(states) => {
			return {
				currentHandler: states.typeAheadState?.currentHandler,
				typeAheadHandler: states.mentionState?.typeAheadHandler,
			};
		},
	);

	const fireAnalyticsEvent = useFireAnalyticsEvent();

	const handleClick = useCallback(() => {
		if (editorApi) {
			editorApi.mention?.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
		}
		fireAnalyticsEvent({
			payload: {
				action: ACTION.CLICKED,
				actionSubject: ACTION_SUBJECT.BUTTON,
				actionSubjectId: ACTION_SUBJECT_ID.AI_PROMPT_MENTION_BUTTON,
				attributes: {
					promptType,
				},
				eventType: EVENT_TYPE.UI,
			},
		});
	}, [editorApi, fireAnalyticsEvent, promptType]);

	const stopPropagationForActionKeys: KeyboardEventHandler = useCallback((event) => {
		if (['Enter', ' ', 'Tab'].includes(event.key)) {
			event.stopPropagation();
		}
	}, []);

	return (
		<IconButton
			isTooltipDisabled={false}
			label={formatMessage(messages.label)}
			testId="mention-button"
			icon={MentionIcon}
			isSelected={
				currentHandler?.id && typeAheadHandler?.id && currentHandler.id === typeAheadHandler.id
			}
			spacing="compact"
			appearance="subtle"
			onClick={handleClick}
			onKeyDown={stopPropagationForActionKeys}
			onKeyUp={stopPropagationForActionKeys}
		/>
	);
};
